import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import toast from 'react-hot-toast';
import { Plus } from 'lucide-react';
import { RecordActions } from '../components/common/RecordActions';
import { RecordViewDialog } from '../components/common/RecordViewDialog';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const TaskListUsage = () => {
  const { canWrite } = useAuth();
  const writable = canWrite('task_list_usages');
  const [viewItem, setViewItem] = useState(null);
  const [taskListUsages, setTaskListUsages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingUsage, setEditingUsage] = useState(null);

  useEffect(() => {
    loadTaskListUsages();
  }, []);

  const loadTaskListUsages = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/task-list-usages`);
      setTaskListUsages(response.data || []);
    } catch (error) {
      console.error('Error loading task list usages:', error);
      toast.error('Failed to load task list usages');
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenDialog = (usage = null) => {
    setEditingUsage(usage);
    setIsDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);

    const data = {
      usage: (formData.get('usage') || '').toUpperCase(),
      description: formData.get('description')
    };

    try {
      if (editingUsage) {
        await axios.put(`${BACKEND_URL}/api/task-list-usages/${editingUsage.usage}`, {
          description: data.description
        });
        toast.success('Task List Usage updated successfully');
      } else {
        await axios.post(`${BACKEND_URL}/api/task-list-usages`, data);
        toast.success('Task List Usage created successfully');
      }
      setIsDialogOpen(false);
      loadTaskListUsages();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save task list usage');
    }
  };

  return (
    <Layout>
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Task List Usages</h1>
            <p className="text-sm text-gray-500 mt-1">Manage task list usage configurations</p>
          </div>
        </div>

        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Task List Usages</h2>
            {writable && (<Button onClick={() => handleOpenDialog()} className="flex items-center gap-2">
              <Plus className="h-4 w-4" />
              Add Task List Usage
            </Button>)}
          </div>

          {isLoading ? (
            <div className="text-center py-8 text-gray-500">Loading...</div>
          ) : taskListUsages.length === 0 ? (
            <div className="text-center py-8 text-gray-500">No task list usages found. Create one to get started.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Usage</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {taskListUsages.map((usage) => (
                    <tr key={usage.usage} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{usage.usage}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{usage.description}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <RecordActions doctype="task_list_usages" onView={() => setViewItem(usage)} onEdit={() => handleOpenDialog(usage)} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editingUsage ? 'Edit Task List Usage' : 'Create Task List Usage'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="usage">Usage * (1-2 characters)</Label>
                <Input
                  id="usage"
                  name="usage"
                  defaultValue={editingUsage?.usage || ''}
                  disabled={editingUsage !== null}
                  required
                  maxLength={2}
                  minLength={1}
                  pattern="[A-Za-z0-9]{1,2}"
                  title="Must be 1-2 alphanumeric characters"
                  className="mt-1 uppercase"
                  placeholder="e.g., 1, 2, P"
                  onInput={(e) => {
                    e.target.value = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 2);
                  }}
                />
                <p className="text-xs text-gray-500 mt-1">1-2 alphanumeric characters</p>
              </div>
              <div>
                <Label htmlFor="description">Description *</Label>
                <Input
                  id="description"
                  name="description"
                  defaultValue={editingUsage?.description || ''}
                  required
                  maxLength={100}
                  className="mt-1"
                />
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit">
                  {editingUsage ? 'Update' : 'Create'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <RecordViewDialog
        open={!!viewItem}
        onOpenChange={(o) => { if (!o) setViewItem(null); }}
        title="Details"
        record={viewItem}
      />
    </Layout>
  );
};

export default TaskListUsage;
